/**
 * Lista compacta de rellamadas pendientes (scheduled_call) para el panel del marcador.
 * Muestra las próximas callbacks del agente con indicador visual si ya vencieron.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  BlockStack,
  Box,
  Button,
  InlineStack,
  Text,
  Badge,
  Spinner,
} from '@/lib/polaris';
import { PhoneIcon } from '@/lib/polaris-icons';
import { supabase } from '@/lib/supabase';
import { format, isPast, isToday } from 'date-fns';
import { es } from 'date-fns/locale';

interface ScheduledCallRow {
  id: string;
  lead_id: string;
  content: string | null;
  scheduled_at: string;
}

interface LeadInfo {
  id: string;
  name: string | null;
  phone: string | null;
}

interface CallbacksPendingListProps {
  tenantId: string;
  userId: string;
  onDialNumber: (phone: string) => void;
}

export function CallbacksPendingList({ tenantId, userId, onDialNumber }: CallbacksPendingListProps) {
  const [rows, setRows] = useState<ScheduledCallRow[]>([]);
  const [leadMap, setLeadMap] = useState<Record<string, LeadInfo>>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('crm_activities')
        .select('id, lead_id, content, scheduled_at')
        .eq('tenant_id', tenantId)
        .eq('type', 'scheduled_call')
        .gte('scheduled_at', new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString())
        .order('scheduled_at', { ascending: true })
        .limit(20);
      if (error) throw error;
      const items = (data ?? []) as ScheduledCallRow[];
      setRows(items);

      const leadIds = [...new Set(items.map((r) => r.lead_id).filter(Boolean))];
      const map: Record<string, LeadInfo> = {};
      if (leadIds.length > 0) {
        const { data: leads } = await supabase
          .from('crm_leads')
          .select('id, name, phone')
          .in('id', leadIds);
        (leads ?? []).forEach((l: LeadInfo) => { map[l.id] = l; });
      }
      setLeadMap(map);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { void load(); }, [load]);

  // Polling cada 60s para detectar callbacks vencidas
  useEffect(() => {
    const iv = setInterval(() => { void load(); }, 60_000);
    return () => clearInterval(iv);
  }, [load]);

  if (loading) {
    return (
      <Box paddingBlock="300">
        <InlineStack align="center"><Spinner size="small" /></InlineStack>
      </Box>
    );
  }

  if (rows.length === 0) return null;

  return (
    <BlockStack gap="200">
      <InlineStack gap="200" blockAlign="center">
        <Text as="h3" variant="headingSm">Rellamadas pendientes</Text>
        <Badge tone="attention">{String(rows.length)}</Badge>
      </InlineStack>
      {rows.map((row) => {
        const lead = leadMap[row.lead_id];
        const dt = new Date(row.scheduled_at);
        const overdue = isPast(dt);
        const today = isToday(dt);
        const timeLabel = today
          ? format(dt, 'HH:mm')
          : format(dt, "d MMM HH:mm", { locale: es });
        const phone = lead?.phone ?? null;

        return (
          <Box key={row.id} paddingBlock="200" paddingInline="0">
            <InlineStack align="space-between" blockAlign="center" gap="200" wrap={false}>
              <BlockStack gap="050">
                <Text as="span" variant="bodyMd" fontWeight="semibold">
                  {lead?.name?.trim() || lead?.phone || 'Lead'}
                </Text>
                <InlineStack gap="100" blockAlign="center">
                  <Text as="span" variant="bodySm" tone={overdue ? 'critical' : 'subdued'}>
                    {overdue ? 'Vencida · ' : ''}{timeLabel}
                  </Text>
                </InlineStack>
                {row.content ? (
                  <Text as="span" variant="bodySm" tone="subdued">
                    {row.content.length > 50 ? row.content.slice(0, 50) + '…' : row.content}
                  </Text>
                ) : null}
              </BlockStack>
              {phone ? (
                <Button
                  icon={PhoneIcon}
                  size="slim"
                  variant="primary"
                  onClick={() => onDialNumber(phone)}
                  accessibilityLabel={`Llamar a ${lead?.name ?? phone}`}
                >
                  Llamar
                </Button>
              ) : null}
            </InlineStack>
          </Box>
        );
      })}
    </BlockStack>
  );
}
